<template>
    <ManagementPage :grid="true" :fullPanel="true" :pageName="$t('Data') + ' / ' + $t('Delete') + ' ' + $tc('Layer', 2)">
        <template v-slot:top-left>
            <div class="container">
                <div class="headers-container">
                    <h3>{{ $t('Delete') + ' ' + $tc('Layer', 2) }}</h3>
                    <p>{{ $t('DeleteLayersMessage') }}</p>
                </div>
                <div
                    v-for="field in fields"
                    :key="field.label"
                    :style="[{ width: '100%', overflow: 'visible' }]"
                >
                    <div class="input-item">
                        <input-field
                            :key="field.varName"
                            :input-type="field.inputType"
                            :value="field.value"
                            :label="field.label"
                            :input-disabled="field.disabled"
                            :select-options-list="field.options"
                            :disableBranchNodes="field.disableBranchNodes"
                            :checkbox-options="field.checkboxOptions"
                            :min="field.min"
                            :max="field.max"
                            :step="field.step"
                            :size="'large'"
                            :clearable="field.clearable"
                            :required="field.required"
                            :inputFieldContainerStyling="{ 'padding': '0' }"
                            :radio-options="field.radioOptions"
                            :placeholder="field.placeholder"
                            :input-style="inputStyle"
                            @input="layer = $event"
                        />
                    </div>
                </div>
                <TableViewWorkspaces
                    ref="tableWorkspacesView"
                    @set-selected-workspace="setSelectedWorkspace"
                />
                <div class="action-buttons">
                    <custom-button
                        :label="$t('Confirm')"
                        :button-color="'#29BDF0'"
                        :label-color="'#FFF'"
                        :disabled="!(layer && (selectedWorkspace.uuid || shapeGeom))"
                        @buttonClick="fetchLayerObjects"
                    />
                </div>
            </div>
            <LoadingSpinner :active.sync="loading" :is-full-page="true" color="#29bdf0" />
        </template>
        <template v-slot:top-right>
            <DeleteLayersMap
                @map-restart="mapRestart"
                @set-shape-geom="shapeGeom=$event"
            />
        </template>
        <template v-slot:bottom-right>
            <TableViewLayerObjects
                :layer=layer
                :workspaceUuid=selectedWorkspace.uuid
                :workspaceGeoJson="selectedWorkspace.geoJson"
                :shapeGeom="shapeGeom"
                @clear-shape-geom="shapeGeom=null"
                @clear-selected-workspace="clearSelectedWorkspace"
                ref="tableLayerObjects"
            />
        </template>
    </ManagementPage>
</template>
  
<script>
  import ManagementPage from "@/newComponents/_Organisms/ManagementPage.vue";
  import TableViewWorkspaces from "@/newComponents/Data/Delete/TableViewWorkspaces.vue"
  import TableViewLayerObjects from "@/newComponents/Data/Delete/TableViewLayerObjects.vue"
  import DeleteLayersMap from "@/newComponents/Data/Delete/DeleteLayersMap.vue"
  import { mapActions } from "vuex";
  import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
  import InputField from "../newComponents/_Atoms/InputField.vue";
  import CustomButton from '@/newComponents/_Atoms/CustomButton';
  import LoadingSpinner from "vue-loading-overlay";
  import { bus } from "@/main.js";
  
  export default {
    name: "DeleteLayers",
  
    components: {
      ManagementPage,
      TableViewWorkspaces,
      InputField,
      TableViewLayerObjects,
      CustomButton,
      DeleteLayersMap,
      LoadingSpinner
    },
  
    mixins: [checkPermissionsMixin],
  
    data() {
      return {
        layer: null,
        layerOptions: [],
        layers: [
            {
                layer: 'fc_building',
                hasExtraFormDataField: false,
                labelField: 'bd_demand_point_address',
                geoJSONField: 'technicalsite.node.nd_geo_json',
                objectListName: 'buildings',
            },
            {
                layer: 'fc_pathway',
                hasExtraFormDataField: false,
                geoJSONField: 'pw_geo_json',
                objectListName: 'pathways',
            },
            {
                layer: 'fc_pole',
                hasExtraFormDataField: true,
                extraFormDataFieldValue: 'poles',
                geoJSONField: 'node.nd_geo_json',
                objectListName: 'technical_points',
            },
            {
                layer: 'fc_manhole',
                hasExtraFormDataField: true,
                extraFormDataFieldValue: 'manholes',
                geoJSONField: 'node.nd_geo_json',
                objectListName: 'technical_points',
            },
            {
                layer: 'fc_cabinet',
                hasExtraFormDataField: true,
                extraFormDataFieldValue: 'cabinets',
                geoJSONField: 'node.nd_geo_json',
                objectListName: 'technical_points',
            },
            {
                layer: 'fc_closure',
                hasExtraFormDataField: false,
                geoJSONField: 'technicalpoint.node.nd_geo_json',
                objectListName: 'closures',
            },
            {
                layer: 'fc_closure_zone',
                hasExtraFormDataField: false,
                geoJSONField: 'clz_geo_json',
                objectListName: 'closures_zones',
            },
            {
                layer: 'fc_fiber_distribution_point',
                hasExtraFormDataField: true,
                extraFormDataFieldValue: 'fdc',
                geoJSONField: 'node.nd_geo_json',
                objectListName: 'technical_sites',
            },
            {
                layer: 'fc_fdp_zone',
                hasExtraFormDataField: false,
                geoJSONField: 'fdpz_geo_json',
                objectListName: 'fiber_distribution_points',
            },
            {
                layer: 'fc_optical_line_termination',
                hasExtraFormDataField: true,
                extraFormDataFieldValue: 'olt',
                geoJSONField: 'node.nd_geo_json',
                objectListName: 'technical_sites',
            },
            {
                layer: 'fc_olt_zone',
                hasExtraFormDataField: false,
                geoJSONField: 'oltz_geo_json',
                objectListName: 'optical_line_terminations',
            },
            {
                layer: 'fc_annotation_point',
                hasExtraFormDataField: false,
                geoJSONField: 'anp_geo_json',
                objectListName: 'annotation_points',
            },
            {
                layer: 'fc_annotation_zone',
                hasExtraFormDataField: false,
                geoJSONField: 'anz_geo_json',
                objectListName: 'annotation_zones',
            },
            {
                layer: 'fc_cartography',
                hasExtraFormDataField: false,
                geoJSONField: 'ctg_geo_json',
                objectListName: 'cartographies',
            },
            {
                layer: 'fc_cable',
                hasExtraFormDataField: true,
                geoJSONField: 'cb_geo_json',
                objectListName: 'cables',
            },
            {
                layer: 'fc_technical_local_mdu',
                hasExtraFormDataField: true,
                geoJSONField: 'technicalSite.node.nd_geo_json',
                objectListName: 'technical_locals_mdu',
            },
            {
                layer: 'fc_technical_local_fdp',
                hasExtraFormDataField: true,
                geoJSONField: 'technicalSite.node.nd_geo_json',
                objectListName: 'technical_locals_fdp',
            },
            {
                layer: 'fc_technical_local_olt',
                hasExtraFormDataField: true,
                geoJSONField: 'technicalSite.node.nd_geo_json',
                objectListName: 'technical_locals_olt',
            },
            {
                layer: 'fc_technical_local_site',
                hasExtraFormDataField: true,
                geoJSONField: 'technicalSite.node.nd_geo_json',
                objectListName: 'technical_locals_site',
            },
            {
                layer: 'fc_toby_box',
                hasExtraFormDataField: true,
                geoJSONField: 'node.nd_geo_json',
                objectListName: 'toby_boxes',
            },
        ],
        inputStyle: {
            'border-radius': '4.1px',
            'border-color': 'rgb(219, 219, 224)'
        },
        selectedWorkspace:{
            uuid: null,
            geoJson: null
        },
        shapeGeom: null,
        loading:false
      }
    },
    mounted() {
        this.getLayersInfo();
    },

    computed: {
        fields(){
            return [
                {
                    icon: "",
                    label: this.$i18n.tc("Layer", 1),
                    value: this.layer,
                    varName: 'layer',
                    inputType: 'select',
                    required: true,
                    options: this.layerOptions,
                    placeholder: "",
                    clearable: true
                },
            ]
        }
    },
  
    methods: {
        ...mapActions(["FETCH_ALL_OBJECTS_PROPERTIES_WITHOUT_FIELDS", "GET_ALL_LISTS"]),

        mapRestart(){
            this.$refs.tableLayerObjects.openClearAllDialog();
        },

        async fetchLayerObjects(){
            await this.$refs.tableLayerObjects.getLayers();
        },

        clearSelectedWorkspace(){
            this.selectedWorkspace = {
                uuid: null,
                geoJson: null
            };
            //use ref to avoid calling bus event in both tables
            this.$refs.tableWorkspacesView.clearSelectedRows();
        },


        setSelectedWorkspace(workspace){
            if(this.selectedWorkspace.uuid === workspace.wp_uuid){
                bus.$emit('clear-map-has-features');
                this.selectedWorkspace = {
                    uuid: null,
                    geoJson: null
                };
            }else{
                this.selectedWorkspace = {
                    uuid: workspace.wp_uuid,
                    geoJson: workspace.wp_geo_json
                };
            }
        },

        async getLayersInfo() {
            this.loading = true;
            try {
                let objectProperties = this.$store.getters.getAllObjectTypesProperties
                if (objectProperties.length === 0) {
                    await this.FETCH_ALL_OBJECTS_PROPERTIES_WITHOUT_FIELDS();
                }
                await this.GET_ALL_LISTS();
                this.updateLayersInfo()
            } catch (error) {
                this.$toasted.global.toast_error(error);
            }finally{
                this.loading = false;
            }
        },

        updateLayersInfo() {
            this.layers.forEach((layer) => {
                layer.endpoint = this.$store.getters.getLayerEndpoint(layer.layer);
                layer.permissionName = this.$store.getters.getLayerPermissionName(layer.layer);
                layer.label = this.getLayerName(layer.layer);
                layer.initials = this.$store.getters.getLayerInitials(layer.layer);
                layer.idField = layer.initials + '_id';
                layer.uuidField = layer.initials + '_uuid';
                if (!layer.hasOwnProperty('labelField')) {
                    layer.labelField = layer.initials + '_label';
                }
                layer.creationDateField = layer.initials + '_created_at';
                layer.updateDateField = layer.initials + '_updated_at';
            })
            let arr = [];
            let numberOfPossibleLayers = this.layers.length;
            for (let i = 0; i < numberOfPossibleLayers; i++) {
                let layer = this.layers[i];
                arr.push({
                    id: layer.layer,
                    label: layer.label,
                    disabled: !this.userHasPermission("module_project_management")
                });
            }
            this.layerOptions = [...arr];
        },

        getLayerName(layer) {
            let label = this.$store.getters.getLayerTranslationKey(layer);
            return this.$i18n.tc(label, 2);
        },
    },
  };
</script>
  
<style scoped>
    .container{
        width: 100%;
        height: 100%;
        border-radius: 8px;
        background: #fff;
        box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
        padding: 19px 19px;
    }

    .headers-container{
        height: 45px;
        display: flex;
        flex-direction: column;
        justify-content: start;
        align-items: start;
        color: #767676;
        font-family: Lato;
        overflow: auto;
    }

    .headers-container h3{
        font-size: 10px;
        font-style: normal;
        font-weight: 800;
        line-height: 20px;
        text-transform: uppercase;
    }

    .headers-container p{
        overflow-x: auto; /* Enable horizontal scrolling */
        white-space: nowrap; /* Prevent line breaks */
        font-size: 10px;
        line-height: 12px;
        font-weight: 600;
        text-align: left;
        margin: 0px;
    }

    .action-buttons {
        display: flex;
        flex-direction: row;
        gap: 10px;
        justify-content: flex-end;
    }
</style>
  


// WEBPACK FOOTER //
// src/views/DeleteLayers.vue